// Dependencies
// -----------------------------------------------
import React from 'react';
import Geosuggest from 'react-geosuggest';
import Script from 'react-load-script';
import styled from 'styled-components';

// Components
// -----------------------------------------------
import Ripple from '../miscellaneous/ripple';

// Styles
// -----------------------------------------------
const LocationForm = styled.div`
  align-items: flex-start;
  box-pack: space-between;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const FormGroup = styled.fieldset`
  display: block;
  width: 100%;

  &.width-25 {
    width: 23%;
  }

  &.width-50 {
    width: 48%;
  }

  &.width-75 {
    width: 73%;
  }
`;

// -----------------------------------------------
// COMPONENT->LOCATION(FORM) ---------------------
// -----------------------------------------------
export default class Location extends React.Component {
  // Constructor
  // ---------------------------------------------
  constructor(props) {
    super(props);

    this.state = {
      adrStreet: this.props.adrStreet,
      adrUnit: this.props.adrUnit,
      adrCity: this.props.adrCity,
      adrState: this.props.adrState,
      adrCountry: this.props.adrCountry,
      adrPostalCode: this.props.adrPostalCode,
      adrCountryShort: this.props.adrCountryShort
    };
  }

  // Component Did Update
  // ---------------------------------------------
  componentDidUpdate(nextProps) {
    if (nextProps !== this.props) {
      this.setState(nextProps);
    }
  }

  // Find Address Part
  // ---------------------------------------------
  findAddressPart = (adrComponents, adr_part) => {
    return adrComponents.find(e => {
      return e.types[0] === adr_part;
    });
  };

  // Update On Suggest Selected
  // ---------------------------------------------
  updateOnSuggestSelected = suggest => {
    try {
      const adrComponents = suggest.gmaps.address_components;
      const adrPostalCode = this.findAddressPart(adrComponents, 'postal_code');
      const adrCountry = this.findAddressPart(adrComponents, 'country');
      const adrState = this.findAddressPart(
        adrComponents,
        'administrative_area_level_1'
      );
      const adrCity = this.findAddressPart(adrComponents, 'locality');
      const adrStreetName = this.findAddressPart(adrComponents, 'route');
      const adrStreetNumber = this.findAddressPart(
        adrComponents,
        'street_number'
      );

      this.setState({
        adrStreet: `${adrStreetNumber ? adrStreetNumber.long_name : ''} ${adrStreetName ? adrStreetName.long_name : ''
          }`,
        adrUnit: '',
        adrCity: adrCity ? adrCity.long_name : '',
        adrState: adrState ? adrState.long_name : '',
        adrCountry: adrCountry ? adrCountry.long_name : '',
        adrPostalCode: adrPostalCode ? adrPostalCode.long_name : '',
        adrCountryShort: adrCountry ? adrCountry.short_name : ''
      });

      this.adrGeoSuggest.update(this.state.adrStreet);

      this.props.setLocationAttributes(this.returnValues());
    } catch (e) {
      this.setState({
        adrStreet: ''
      });
    }
  };

  // Update On Change
  // ---------------------------------------------
  updateOnChange = e => {
    const stateChange = {};
    stateChange[e.target.name] = e.target.value;
    this.setState(stateChange, () => {
      this.props.setLocationAttributes(this.returnValues());
    });
  };

  // Return Values
  // ---------------------------------------------
  returnValues = () => {
    const values = this.state;

    return values;
  };

  addErrorClass = key => {
    if (this.props.lynnbrookErrors?.length) {
      if (this.props.lynnbrookErrors.includes(key)) return 'field-error';
    }

    return '';
  };

  // Render Geo Suggested Address
  // ---------------------------------------------
  renderGeoSuggestedAddress = () => {
    const {
      adrStreet,
      adrUnit,
      adrCity,
      adrState,
      adrCountry,
      adrPostalCode
    } = this.state;

    const {
      savedAdrStreet,
      savedAdrUnit,
      savedAdrCity,
      savedAdrState,
      savedAdrCountry,
      savedAdrPostalCode
    } = this.props;

    return (
      <LocationForm>
        <FormGroup>
          <label>
            <span>Address</span>
          </label>
          <Geosuggest
            inputClassName={`magnified ${this.addErrorClass('adrStreet')}`}
            placeholder="123 Road St."
            types={['geocode']}
            suggestsHiddenClassName="hidden"
            initialValue={adrStreet || savedAdrStreet}
            ref={node => {
              this.adrGeoSuggest = node;
            }}
            onSuggestSelect={this.updateOnSuggestSelected}
          />
          <input
            className="magnified"
            type="text"
            name="adrUnit"
            placeholder="Ste. 1000"
            style={{ marginTop: '-8px' }}
            value={adrUnit || savedAdrUnit}
            onChange={this.updateOnChange}
          />
        </FormGroup>
        <FormGroup className="width-50">
          <label htmlFor="adrCity">
            <span>City</span>
          </label>
          <input
            className={`magnified ${this.addErrorClass('adrCity')}`}
            type="text"
            name="adrCity"
            placeholder="Chicago"
            value={adrCity || savedAdrCity}
            onChange={this.updateOnChange}
          />
        </FormGroup>
        <FormGroup className="width-25">
          <label htmlFor="adrState">
            <span>State / Province</span>
          </label>
          <input
            className={`magnified ${this.addErrorClass('adrState')}`}
            type="text"
            name="adrState"
            placeholder="IL"
            value={adrState || savedAdrState}
            onChange={this.updateOnChange}
          />
        </FormGroup>
        <FormGroup className="width-25">
          <label htmlFor="adrPostalCode">
            <span>Postal Code</span>
          </label>
          <input
            className={`magnified ${this.addErrorClass('adrPostalCode')}`}
            type="text"
            name="adrPostalCode"
            placeholder="60622"
            value={adrPostalCode || savedAdrPostalCode}
            onChange={this.updateOnChange}
          />
        </FormGroup>
        <FormGroup>
          <label htmlFor="adrCountry">
            <span>Country</span>
          </label>
          <input
            className={`magnified ${this.addErrorClass('adrCountry')}`}
            type="text"
            name="adrCountry"
            placeholder={this.props.defaultCountry || `United States`}
            value={adrCountry || savedAdrCountry}
            onChange={this.updateOnChange}
          />
        </FormGroup>
      </LocationForm>
    );
  };

  // Render
  // ---------------------------------------------
  render() {
    return <>{this.renderGeoSuggestedAddress()}</>;
  }
}
